import { Component, inject } from '@angular/core';
import { IsPhaseAtLeast, OEP_ENTITY, OEP_LIVE_FILTER } from 'agu_oep_core';
import { OEPDataService, type OEPRowsQuery } from '../../data/oep-data.service';
import { OEPFilesService } from '../../data/oep-files.service';
import { OEPViewBase } from '../../state/oep-view-base';
import type { OEPStatusLine } from '../shared/status';
import { OEP_TAB_BLOCKED, OEPOpenInNewTab } from '../shared/open-tab';
import { OEPLatestFiles, OEPPresentationLinkSql, OEPUploadedFilesSql } from '../shared/files';
import { BuildNominateVM, type NominateVM, type OEPNomPresenterRowVM, type OEPNomSessionInput } from './nominate.vm';
import { Str, StrOrNull, Up } from '../shared/rows';


/** The sessions I nominate for this season (Session Nominators, RLS-scoped to me — T5). */
export function OEPMySessionsSql(me: string, seasonID: string): string {
    return `SELECT SessionID FROM FeedbackPlatform.SessionNominator WHERE UserID='${OEPDataService.G(me)}' AND SessionID IN (SELECT ID FROM FeedbackPlatform.[Session] WHERE SeasonID='${OEPDataService.G(seasonID)}')`;
}

/**
 * Nominator sessions ("Nominate a presenter for an award") — markup.html:661-709. All copy and rules
 * live in nominate.vm.ts (pure, spec'd); this gathers NominateInputs in ONE RunViews batch
 * (OEPDataService.Batch; every row RLS-scoped to the nominator, T5) and joins on the client, so
 * there is no per-session or per-presenter query. Only OEP Final files are asked for (and only once
 * finals can exist); drafts, reviews and reviewer feedback are never loaded here. Finals open
 * through OEPFilesService.DownloadUrl via OEPOpenInNewTab.
 */
@Component({
    standalone: false,
    selector: 'oep-nominate',
    templateUrl: './oep-nominate.component.html',
    styleUrl: './oep-nominate.component.scss',
})
export class OEPNominateComponent extends OEPViewBase {
    private readonly files = inject(OEPFilesService);

    VM: NominateVM | null = null;
    /** Result of opening a final file, announced in the aria-live line. */
    Status: OEPStatusLine | null = null;

    protected async Load(): Promise<void> {
        const season = this.Context.Season;
        if (!season) return;
        const me = OEPDataService.G(this.Context.Me.UserID);
        const sid = OEPDataService.G(season.ID);
        const mySessions = OEPMySessionsSql(me, sid);
        const presWhere = `SeasonID='${sid}' AND ${OEP_LIVE_FILTER.Presentation} AND SessionID IN (${mySessions})`;
        const pres = `SELECT ID FROM FeedbackPlatform.Presentation WHERE ${presWhere}`;
        const link = OEPPresentationLinkSql(pres);
        // Finals are uploaded from FeedbackReleased on (FinalUpload window); before that there is nothing to ask for.
        const finals = IsPhaseAtLeast(season.Phase, 'FeedbackReleased');
        const queries: OEPRowsQuery[] = [
            { EntityName: OEP_ENTITY.Sessions, ExtraFilter: `SeasonID='${sid}' AND ID IN (${mySessions})`, Fields: ['ID', 'Code', 'Title', 'Format'], OrderBy: 'Code, ID' },
            { EntityName: OEP_ENTITY.Presentations, ExtraFilter: presWhere, Fields: ['ID', 'Title', 'SessionID', 'Format', 'PresentationCode', 'PresenterUserID', 'PresenterUser'], OrderBy: 'PresentationCode, Title, ID' },
            // The presenters' rows (institution, career stage) — readable by their session's nominators (T5).
            { EntityName: OEP_ENTITY.Participants, ExtraFilter: `SeasonID='${sid}' AND UserID IN (SELECT PresenterUserID FROM FeedbackPlatform.Presentation WHERE ID IN (${pres}))`, Fields: ['UserID', 'Institution', 'CareerStage'] },
            { EntityName: OEP_ENTITY.Nominations, ExtraFilter: `SeasonID='${sid}' AND NominatorUserID='${me}' AND PresentationID IN (${pres})`, Fields: ['PresentationID', 'Status'] },
            ...(finals ? [
                { EntityName: OEP_ENTITY.Files, ExtraFilter: OEPUploadedFilesSql(pres, ['Final']), Fields: ['ID', 'Name', 'Category', 'Status', 'ContentType', '__mj_CreatedAt'], OrderBy: '__mj_CreatedAt DESC' },
                { EntityName: OEP_ENTITY.FileEntityRecordLinks, ExtraFilter: link, Fields: ['FileID', 'RecordID'] },
            ] : []),
        ];
        const [sessions, presentations, participants, nominations, fileRows = [], links = []] = await this.Data.Batch(queries);

        const participantOf = new Map(participants.map((x) => [Up(Str(x, 'UserID')), x]));
        const submitted = new Set(nominations.filter((n) => Str(n, 'Status') === 'Submitted').map((n) => Up(Str(n, 'PresentationID'))));
        const finalOf = OEPLatestFiles(fileRows, links, 'Final');

        const items: OEPNomSessionInput[] = sessions.map((s) => ({
            SessionID: Str(s, 'ID'),
            Code: Str(s, 'Code'),
            Title: Str(s, 'Title'),
            Presenters: presentations.filter((p) => Up(Str(p, 'SessionID')) === Up(Str(s, 'ID'))).map((p) => {
                const pid = Up(Str(p, 'ID'));
                const who = participantOf.get(Up(Str(p, 'PresenterUserID')));
                return {
                    PresentationID: Str(p, 'ID'),
                    Title: Str(p, 'Title'),
                    PresenterName: Str(p, 'PresenterUser'),
                    Institution: who ? StrOrNull(who, 'Institution') : null,
                    CareerStage: who ? StrOrNull(who, 'CareerStage') : null,
                    Format: StrOrNull(p, 'Format') ?? StrOrNull(s, 'Format'),
                    Code: StrOrNull(p, 'PresentationCode'),
                    FinalFileID: finalOf.get(pid)?.FileID ?? null,
                    Submitted: submitted.has(pid),
                };
            }),
        }));

        this.VM = BuildNominateVM({
            Phase: season.Phase, Timeline: season.Timeline, SeasonCode: season.Code,
            Sessions: items,
            Resources: this.Context.Resources.nominator ?? [],
        });
    }

    Open(row: OEPNomPresenterRowVM): void {
        if (row.CtaEnabled) this.Go('nomination', row.PresentationID);
    }

    /** "View final presentation": the tab opens inside the click (OEPOpenInNewTab), then gets the signed URL. */
    async ViewFinal(row: OEPNomPresenterRowVM): Promise<void> {
        const fileID = row.FinalFileID;
        if (fileID === null) return;
        this.Status = null;
        try {
            const tab = await OEPOpenInNewTab(() => this.files.DownloadUrl(fileID));
            if (tab.Blocked) this.Status = { Text: OEP_TAB_BLOCKED, Tone: 'error', Link: { Label: 'Open the file', Url: tab.Url } };
        } catch (err) {
            this.Status = { Text: err instanceof Error ? err.message : String(err), Tone: 'error' };
        }
        this.Changed();
    }
}
